import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import CapabilityDisclosure from '@components/capabilities/CapabilityDisclosure';
import type { CapabilityStatus } from '@hooks/useCapabilities';
import type { CadCapability } from '@services/settings.service';

interface CadLimitsPanelProps {
  status: CapabilityStatus;
  cad?: CadCapability;
  onRetry: () => void;
}

/** CAD access is per-tenant, so a disabled tenant gets a reason, not limits it cannot use. */
const CadLimitsPanel: React.FC<CadLimitsPanelProps> = ({ status, cad, onRetry }) => {
  const { t } = useTranslation();

  if (status === 'ready' && cad && !cad.enabled) {
    return (
      <Alert>
        <AlertDescription className="revy-cjk leading-[1.6]">
          {t('cad.limitsDisabled')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <CapabilityDisclosure
      status={status}
      hasValues={Boolean(cad)}
      titleKey="cad.limitsTitle"
      onRetry={onRetry}
    >
      {cad && (
        <>
          <li>{t('cad.limitsFormats', { types: cad.allowedExtensions })}</li>
          <li>{t('cad.limitsMaxSize', { size: cad.maxFileSizeMb })}</li>
          <li>{t('cad.limitsBatch', { count: cad.maxBatchFiles })}</li>
          <li>
            {cad.thicknessEnabled
              ? t('cad.limitsThicknessEnabled')
              : t('cad.limitsThicknessDisabled')}
          </li>
        </>
      )}
    </CapabilityDisclosure>
  );
};

export default CadLimitsPanel;
